import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import {
  CheckCircle2, XCircle, Award, ExternalLink, ShieldCheck, Loader2,
  Calendar, Link2, Share2, BadgeCheck, IdCard, Hexagon,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Public certificate verification page.
 * Backed by GET /api/certifications/verify/{credential_id} — no auth required.
 *
 * Two-column layout:
 *   • Left column: Verified-credential hero + Credential ID / Issued /
 *     Certification / Tier detail tiles + Issued By card + About This
 *     Credential card.
 *   • Right column: rendered HTML certificate populated with the
 *     dynamic values, followed by two CTAs (Explore CPG Institute →
 *     cpg.institute · Earn your own credential → /pricing).
 *
 * The main marketing site lives at https://cpg.institute (an env var
 * overrides in case the domain ever changes without a redeploy).
 */
const CPG_INSTITUTE_HOME = process.env.REACT_APP_MARKETING_SITE_URL || "https://cpg.institute";
// The Pricing page for prospective founders lives on the app domain
// (`courses.cpg.institute`). Use an env override so the redirect stays
// correct in preview (where REACT_APP_APP_SITE_URL points at the
// preview host) and production.
const CPG_APP_HOME = process.env.REACT_APP_APP_SITE_URL || "https://courses.cpg.institute";
const CPG_PRICING_URL = `${CPG_APP_HOME.replace(/\/$/, "")}/pricing`;

export default function VerifyCredential() {
  const { credentialId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!credentialId) return;
    setState({ loading: true, data: null, error: null });
    api.get(`/certifications/verify/${credentialId}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: formatError(e) }));
  }, [credentialId]);

  const { loading, data, error } = state;

  const share = async () => {
    const url = window.location.href;
    const title = data ? `${data.recipient_name} — ${data.cert_name}` : "CPG Institute credential";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — copy from the address bar");
    }
  };

  return (
    <div className="min-h-screen bg-shell">
      {/* Public sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <a
            href={CPG_INSTITUTE_HOME}
            target="_blank"
            rel="noopener noreferrer"
            className="font-display text-lg tracking-tight flex items-center gap-2"
            data-testid="verify-brand"
          >
            <span className="w-6 h-6 rounded-full grad-band" /> CPG Institute
            <span className="hidden sm:inline text-[10px] font-bold tracking-[0.22em] text-muted-foreground uppercase ml-1">
              by Brand Vault 360
            </span>
          </a>
          {data ? (
            <button
              type="button"
              onClick={share}
              data-testid="verify-share"
              className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-sm font-semibold text-foreground hover:border-primary/40 hover:text-primary transition"
            >
              <Share2 className="w-4 h-4" /> Share Certificate
            </button>
          ) : (
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Verify a credential
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {loading && (
          <div className="surface p-10 text-center max-w-2xl mx-auto" data-testid="verify-loading">
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
            <p className="mt-3 text-sm text-muted-foreground">Verifying credential…</p>
          </div>
        )}

        {!loading && error && (
          <div className="surface p-10 text-center max-w-2xl mx-auto" data-testid="verify-invalid">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 text-red-600 grid place-items-center">
              <XCircle className="w-7 h-7" />
            </div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-red-600 mt-4">Credential not found</p>
            <h1 className="font-display text-3xl mt-1">This ID doesn't match a CPG Institute credential.</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              The credential ID <span className="font-mono">{credentialId}</span> was not found in the CPG Institute registry. It may be
              mistyped, revoked, or not yet issued.
            </p>
            <a
              href={CPG_INSTITUTE_HOME}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-6 inline-flex"
              data-testid="verify-home-cta"
            >
              Back to CPG Institute
            </a>
          </div>
        )}

        {!loading && data && (
          <div className="grid lg:grid-cols-[360px,minmax(0,1fr)] gap-6 lg:gap-8">
            {/* ── LEFT COLUMN — meta ─────────────────────────────── */}
            <aside className="space-y-4" data-testid="verify-valid">
              {/* Verified hero card */}
              <div className="rounded-2xl p-6 text-white relative overflow-hidden"
                   style={{ background: "linear-gradient(135deg, #d63384 0%, #6366f1 100%)" }}>
                <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center mb-4">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/85 font-bold">Verified credential</p>
                <h1 className="font-display text-3xl leading-tight mt-1">
                  {data.recipient_name}
                </h1>
                <p className="mt-3 text-sm text-white/95">
                  has earned the <span className="font-semibold">{data.cert_name}</span> from CPG Institute.
                </p>
              </div>

              {/* Detail tiles */}
              <div className="grid grid-cols-2 gap-3">
                <DetailTile label="Credential ID" icon={IdCard} testid="verify-cred-id">
                  <span className="font-mono text-xs break-all">{data.credential_id}</span>
                </DetailTile>
                <DetailTile label="Issued" icon={Calendar} testid="verify-issued">
                  {formatDate(data.issued_at)}
                </DetailTile>
                <DetailTile label="Certification" icon={BadgeCheck} testid="verify-cert">
                  {data.cert_name}
                </DetailTile>
                <DetailTile label="Level" icon={Hexagon} testid="verify-tier">
                  {data.cert_tier === "primary" ? "Master certification" : "Specialist certification"}
                </DetailTile>
              </div>

              {/* Issued By */}
              <div className="rounded-2xl border border-shell-border bg-white p-5" data-testid="verify-issued-by">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Issued by</p>
                <div className="mt-3 flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                       style={{ background: "linear-gradient(135deg, #4f46e5, #6366f1)" }}>
                    <Hexagon className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="font-semibold text-foreground">CPG Institute</p>
                      <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      The leading CPG education platform with 25+ years of CPG experience.
                    </p>
                  </div>
                </div>
                <a
                  href={CPG_INSTITUTE_HOME}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-shell-border px-3 py-2 text-xs font-semibold hover:border-primary/40 hover:text-primary transition"
                  data-testid="verify-visit-website"
                >
                  <Link2 className="w-3.5 h-3.5" /> Visit Website
                </a>

                <div className="border-t border-shell-border mt-5 pt-5">
                  <p className="text-[10px] uppercase tracking-widest text-primary font-bold" data-testid="verify-about-heading">
                    About this credential
                  </p>
                  <p className="mt-2 text-sm text-foreground leading-relaxed">
                    We honor the holder of this certificate and badge with active participation in our programs and community.
                  </p>
                </div>
              </div>
            </aside>

            {/* ── RIGHT COLUMN — certificate ─────────────────────── */}
            <section>
              <Certificate credentialId={data.credential_id} />

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={CPG_INSTITUTE_HOME}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="verify-explore"
                  className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-5 py-2.5 text-sm font-semibold text-foreground hover:border-primary/40 hover:text-primary transition"
                >
                  <ExternalLink className="w-4 h-4" /> Explore CPG Institute
                </a>
                <a
                  href={CPG_PRICING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="verify-become"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition"
                >
                  <Award className="w-4 h-4" /> Earn your own credential
                </a>
              </div>

              <p className="mt-6 text-xs text-muted-foreground text-center max-w-2xl mx-auto">
                This credential was issued by CPG Institute and is verifiable via the QR code on the printed certificate.
                <br />Signed by Warren Ezra, Founder.
              </p>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function DetailTile({ label, icon: Icon, testid, children }) {
  return (
    <div className="rounded-xl border border-shell-border bg-white p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </p>
      <p className="mt-1.5 font-semibold text-foreground text-sm">{children}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
}

/* ── Rendered certificate ─────────────────────────────────────────────
 * Iframes the backend-rendered certificate HTML (same template used
 * for the PDF download) so the design/fonts/seal/wordmarks always
 * match. The template is authored at a fixed 1440×1020 landscape
 * canvas; we render the iframe at its natural 1440px width and use a
 * CSS transform to scale it down proportionally to the container, so
 * the full certificate is always visible without clipping.
 * ────────────────────────────────────────────────────────────────── */
function Certificate({ credentialId }) {
  const src = `${process.env.REACT_APP_BACKEND_URL || ""}/api/certifications/verify/${credentialId}/certificate.html`;
  const wrapRef = React.useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const NATURAL_W = 1440;
    const compute = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / NATURAL_W);
    };
    compute();
    // ResizeObserver keeps the scale in sync while the user resizes
    // the browser window; a plain `resize` listener would miss
    // container width changes that don't touch the viewport.
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const NATURAL_W = 1440;
  const NATURAL_H = 1020;
  const scaledH = Math.max(NATURAL_H * scale, 200); // reserve height once scale is known

  return (
    <div
      ref={wrapRef}
      className="relative rounded-2xl overflow-hidden shadow-xl bg-white"
      data-testid="verify-certificate"
      style={{ height: `${scaledH}px` }}
    >
      <iframe
        src={src}
        title="Certificate"
        loading="lazy"
        className="border-0 bg-transparent"
        data-testid="verify-certificate-frame"
        style={{
          width: `${NATURAL_W}px`,
          height: `${NATURAL_H}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          display: "block",
        }}
      />
    </div>
  );
}


